<template>
  <div class="expandable-text-container">
    <!-- 隐藏状态：显示截断文本 + 展开按钮 -->
    <div v-if="isHide" class="hide-container">
      <div ref="contentRef" class="sunui-grand-summary" :style="hideStyle">
        <span :class="props.customClass || 'default-content'">
          <slot>{{ dt || '' }}</slot>
        </span>
      </div>

      <!-- 展开按钮 -->
      <div
        v-if="enableButton && lines > Number(line)"
        class="sunui-grand-show-btn"
        @click.stop="isHide = false"
      >
        <span class="expand">...</span>
        <span class="expand expand-text">{{ expandText }}</span>
      </div>

      <!-- 测量用：仅客户端挂载后渲染，避免 SSR 重复内容 -->
      <div v-if="enableButton && isMounted" ref="fullHeightRef" class="sunui-grand-full-height">
        <span :class="props.customClass || 'default-content'">
          <slot>{{ dt || '' }}</slot>
        </span>
      </div>
    </div>

    <!-- 展开状态：显示全文 + 收起按钮 -->
    <div v-else class="show-container">
      <span :class="props.customClass || 'default-content'">
        <slot>{{ dt || '' }}</slot>

        <!-- 收起按钮显示在文字末尾 -->
        <span v-if="foldHint" class="expand fold-hint" @click.stop="isHide = true">
          {{ foldHint }}
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, nextTick } from 'vue';

  // Props
  const props = withDefaults(
    defineProps<{
      line?: number | string;
      dt?: string;
      enableButton?: boolean;
      expandText?: string;
      foldHint?: string;
      customClass?: string;
    }>(),
    {
      line: 1,
      dt: '',
      enableButton: true,
      expandText: '展开',
      foldHint: '收起',
      customClass: '',
    },
  );

  // Refs
  const isHide = ref(true);
  const isMounted = ref(false);
  const textHeight = ref(0);
  const lineHeight = ref(20); // 默认行高，避免除以0
  const contentRef = ref<HTMLDivElement | null>(null);
  const fullHeightRef = ref<HTMLDivElement | null>(null);

  // 计算隐藏状态的样式
  const hideStyle = computed(() => ({
    '-webkit-line-clamp': isHide.value ? props.line : 'unset',
  }));

  // 计算总行数
  const lines = computed(() => {
    if (!props.enableButton) return Number(props.line);
    if (textHeight.value > 0 && lineHeight.value > 0) {
      return Math.ceil(textHeight.value / lineHeight.value);
    }
    return 0;
  });

  const measureTextHeight = async () => {
    if (!props.enableButton) return;

    await nextTick();
    await new Promise((r) => setTimeout(r, 50));

    if (fullHeightRef.value) {
      const rect = fullHeightRef.value.getBoundingClientRect();
      textHeight.value = rect.height;
    }

    if (contentRef.value) {
      const computedStyle = window.getComputedStyle(contentRef.value);
      const lineHeightValue = computedStyle.lineHeight;
      lineHeight.value = parseInt(lineHeightValue, 10) || 20;
    }
  };

  onMounted(async () => {
    if (!props.enableButton) return;

    isMounted.value = true;
    await nextTick();
    await measureTextHeight();
  });
</script>

<style scoped>
  .default-content {
    font-family: 'Source Han Sans', '思源黑体', sans-serif;
    font-size: 11px;
    color: #989ba6;
  }

  .expandable-text-container {
    width: 100%;
  }

  /* 隐藏状态容器 */
  .hide-container {
    position: relative;
    width: 100%;
  }

  /* 截断文本样式 */
  .sunui-grand-summary {
    width: 100%;
    overflow: hidden;
    text-overflow: clip;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  /* 测量完整高度用，不参与视觉布局 */
  .sunui-grand-full-height {
    position: absolute;
    visibility: hidden;
    overflow: visible;
    white-space: normal;
  }

  /* 展开按钮样式 */
  .sunui-grand-show-btn {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    text-align: right;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 50%);
    padding-left: 0.5rem;
    cursor: pointer;
  }

  .sunui-grand-show-btn .expand {
    font-weight: 500;
    cursor: pointer;
  }

  .expand-text {
    color: #455bd9;
    padding-left: 4px;
    display: inline-block;
  }

  /* 展开状态容器 */
  .show-container {
    width: 100%;
  }

  /* 收起按钮样式 */
  .fold-hint {
    color: #455bd9;
    font-weight: 500;
    cursor: pointer;
    margin-left: 4px;
    display: inline;
  }
</style>
